import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { api } from '../../hooks/useApi';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminMobileNav from '../../components/admin/AdminMobileNav';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  ArrowLeft, Percent, Mail, MessageSquare, Save, CreditCard, HardDrive, MapPin,
  AlertTriangle, CheckCircle, X, Bell, Loader2, Gift, Award, Crown, Star, Plus, Trash2,
  Activity, RefreshCw, Bug, Database, Server, ChevronDown, ChevronUp, Shield, Wallet, UserPlus
} from 'lucide-react';

// Extracted Settings Tab Components
import {
  CommissionSettings,
  PaymongoSettings,
  StorageSettings,
  SMSSettings,
  EmailSettings,
  MapsSettings,
  FirebaseSettings,
  ServiceAreaSettings,
  MaintenanceSettings,
  ReferralSettings,
  ProviderLoyaltySettings,
  InsuranceSettings,
  BulkDiscountSettings,
  TaxSettings,
  WalletSettings,
  SignupSettings
} from './settings';

export default function AdminSettings() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('commission');
  
  // Settings state
  const [commission, setCommission] = useState({
    global_rate: 15,
    category_rates: {},
    enabled: true
  });
  
  const [smtp, setSMTP] = useState({
    host: '',
    port: 587,
    username: '',
    password: '',
    from_email: '',
    from_name: '',
    reply_to: '',
    enabled: false
  });

  const [resend, setResend] = useState({
    enabled: false,
    api_key: '',
    from_email: '',
    from_name: '',
    reply_to: ''
  });
  
  const [sms, setSMS] = useState({
    provider: 'twilio',
    api_key: '',
    api_secret: '',
    sender_id: '',
    enabled: false
  });
  const [paymongo, setPayMongo] = useState({
    public_key: '',
    secret_key: '',
    webhook_secret: '',
    environment: 'test',
    enabled: false,
    fee_settings: {
      card: { mdr: 3.5, fixed: 15 },
      gcash: { mdr: 2.0, fixed: 0 },
      maya: { mdr: 2.0, fixed: 0 }
    }
  });
  
  const [storage, setStorage] = useState({
    provider: 'emergent',
    access_key: '',
    secret_key: '',
    bucket_name: '',
    region: '',
    endpoint_url: '',
    use_emergent_storage: true,
    enabled: true
  });
  
  const [maps, setMaps] = useState({
    provider: 'google',
    api_key: '',
    enabled: true
  });

  const [firebase, setFirebase] = useState({
    api_key: '',
    auth_domain: '',
    project_id: '',
    storage_bucket: '',
    messaging_sender_id: '',
    app_id: '',
    vapid_key: '',
    server_key: '',
    service_account_json: '',
    enabled: false
  });

  const [serviceArea, setServiceArea] = useState({
    max_radius_km: 50,
    default_radius_km: 10
  });

  // Maintenance mode state
  const [maintenance, setMaintenance] = useState({
    enabled: false,
    title: 'Under Maintenance',
    message: "We're currently performing scheduled maintenance. Please check back soon.",
    allowed_roles: ['admin', 'manager']
  });

  // Referral program state
  const [referral, setReferral] = useState({
    enabled: true,
    reward_amount: 100,
    required_bookings: 5,
    description: 'Refer a friend to HandyBayani! When your recruit completes 5 successful paid bookings, both of you will receive Php 100 as a reward.',
    mechanics: []
  });

  // Provider Loyalty Program state
  const [providerLoyalty, setProviderLoyalty] = useState({
    enabled: false,
    tiers: [
      { name: 'Bronze', min_jobs: 0, discount_percentage: 0, priority_booking: false, badge_color: 'bronze' },
      { name: 'Silver', min_jobs: 25, discount_percentage: 5, priority_booking: false, badge_color: 'silver' },
      { name: 'Gold', min_jobs: 50, discount_percentage: 10, priority_booking: true, badge_color: 'gold' },
      { name: 'Platinum', min_jobs: 100, discount_percentage: 15, priority_booking: true, badge_color: 'platinum' }
    ],
    booking_multiplier: 1.0,
    rating_bonus: 50,
    completion_bonus: 50,
    monthly_bonus_threshold: 20,
    monthly_bonus_amount: 500,
    description: 'Earn points and unlock benefits by completing more jobs on the platform!'
  });

  // Insurance settings state
  const [insurance, setInsurance] = useState({
    enabled: false,
    customer_insurance_enabled: true,
    customer_insurance_mandatory: false,
    customer_pricing_type: 'percentage',
    customer_flat_fee: 50,
    customer_percentage: 5,
    customer_coverage_limit: 10000,
    customer_terms: 'Service Guarantee Insurance covers property damage during service, incomplete service, and provider no-show.',
    customer_covers_property_damage: true,
    customer_covers_incomplete_service: true,
    customer_covers_no_show: true,
    provider_insurance_enabled: true,
    provider_insurance_mandatory: false,
    provider_pricing_type: 'flat',
    provider_flat_fee: 25,
    provider_percentage: 2,
    provider_coverage_limit: 50000,
    provider_terms: 'Accident Insurance covers provider injuries during service delivery.'
  });

  // Bulk Booking Discount settings
  const [bulkDiscount, setBulkDiscount] = useState({
    enabled: false,
    tiers: [
      { min_units: 3, discount_percentage: 5 },
      { min_units: 5, discount_percentage: 10 },
      { min_units: 8, discount_percentage: 15 }
    ]
  });

  // Tax settings
  const [tax, setTax] = useState({
    tax_rate: 0.12,
    tax_name: 'VAT',
    tax_enabled: true,
    tax_registration_number: '',
    tax_display_on_invoice: true,
    tax_included_in_price: false
  });

  // Wallet settings
  const [wallet, setWallet] = useState({
    min_topup_amount: 100,
    min_provider_balance: 100,
    topup_presets: [100, 500, 1000, 2000]
  });

  // Test email state
  const [testEmail, setTestEmail] = useState('');
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [resendTestEmail, setResendTestEmail] = useState('');
  const [isSendingResendTest, setIsSendingResendTest] = useState(false);

  // Health check / debugging state
  const [healthData, setHealthData] = useState(null);
  const [isCheckingHealth, setIsCheckingHealth] = useState(false);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [lastSaveResponse, setLastSaveResponse] = useState(null);
  
  // Backup / Restore state
  const [backups, setBackups] = useState([]);
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [isLoadingBackups, setIsLoadingBackups] = useState(false);
  const [backupSettings, setBackupSettings] = useState({
    daily_backup_enabled: true,
    retention_days: 30
  });
  const [restoreOptions, setRestoreOptions] = useState({
    restore_users: false,
    restore_transactions: false
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.getSettings();
        if (response.data.commission) {
          setCommission({
            global_rate: (response.data.commission.global_rate || 0.15) * 100,
            category_rates: response.data.commission.category_rates || {},
            enabled: response.data.commission.enabled !== false
          });
        }
        if (response.data.smtp) {
          setSMTP(prev => ({ ...prev, ...response.data.smtp }));
        }
        if (response.data.resend) {
          setResend(prev => ({ ...prev, ...response.data.resend }));
        }
        if (response.data.sms) {
          setSMS(prev => ({ ...prev, ...response.data.sms }));
        }
        if (response.data.paymongo) {
          setPayMongo(prev => ({ ...prev, ...response.data.paymongo }));
        }
        if (response.data.storage) {
          setStorage(prev => ({ ...prev, ...response.data.storage }));
        }
        if (response.data.maps) {
          setMaps(prev => ({ ...prev, ...response.data.maps }));
        }
        if (response.data.firebase) {
          setFirebase(prev => ({ ...prev, ...response.data.firebase }));
        }
        // Fetch service area config
        try {
          const areaRes = await axios.get(`${API}/admin/settings/service-area`);
          if (areaRes.data) setServiceArea(prev => ({ ...prev, ...areaRes.data }));
        } catch (e) {
          console.error('Settings: failed to fetch service area config', e);
        }
        
        // Fetch maintenance mode
        try {
          const maintRes = await axios.get(`${API}/admin/maintenance`);
          if (maintRes.data) setMaintenance(prev => ({ ...prev, ...maintRes.data }));
        } catch (e) {
          console.error('Settings: failed to fetch maintenance mode', e);
        }
        
        // Fetch referral settings
        try {
          const refRes = await axios.get(`${API}/referral/settings`);
          if (refRes.data) setReferral(prev => ({ ...prev, ...refRes.data }));
        } catch (e) {
          console.error('Settings: failed to fetch referral settings', e);
        }
        
        // Fetch provider loyalty settings
        try {
          const loyaltyRes = await axios.get(`${API}/provider-loyalty/settings`);
          if (loyaltyRes.data && loyaltyRes.data.enabled !== undefined) {
            setProviderLoyalty(prev => ({ ...prev, ...loyaltyRes.data }));
          }
        } catch (e) {
          console.error('Settings: failed to fetch provider loyalty settings', e);
        }
        
        // Fetch insurance settings
        try {
          const insuranceRes = await axios.get(`${API}/admin/insurance/settings`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          if (insuranceRes.data) setInsurance(prev => ({ ...prev, ...insuranceRes.data }));
        } catch (e) {
          console.error('Settings: failed to fetch insurance settings', e);
        }
        
        // Fetch bulk discount settings
        try {
          const bulkDiscountRes = await axios.get(`${API}/admin/bulk-discount/settings`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          if (bulkDiscountRes.data) setBulkDiscount(prev => ({ ...prev, ...bulkDiscountRes.data }));
        } catch (e) {
          console.error('Settings: failed to fetch bulk discount settings', e);
        }
        
        // Fetch tax settings
        try {
          const taxRes = await axios.get(`${API}/tax-settings`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          if (taxRes.data) setTax(prev => ({ ...prev, ...taxRes.data }));
        } catch (e) {
          console.error('Settings: failed to fetch tax settings', e);
        }
        
        // Fetch wallet settings
        try {
          const walletRes = await axios.get(`${API}/wallet-settings`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          if (walletRes.data) setWallet(prev => ({ ...prev, ...walletRes.data }));
        } catch (e) {
          console.error('Settings: failed to fetch wallet settings', e);
        }
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSettings();
  }, []);

  // Health Check Function
  const handleHealthCheck = async () => {
    setIsCheckingHealth(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/settings/health`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHealthData(response.data);
      setShowDebugPanel(true);
      toast.success('Health check complete - review the debug panel below');
    } catch (error) {
      console.error('Health check failed:', error);
      const errorDetail = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Health check failed: ${errorDetail}`);
      setHealthData({ error: errorDetail, timestamp: new Date().toISOString() });
      setShowDebugPanel(true);
    } finally {
      setIsCheckingHealth(false);
    }
  };

  // Backup functions
  const fetchBackups = async () => {
    setIsLoadingBackups(true);
    try {
      const token = localStorage.getItem('token');
      const [backupsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/admin/backup/list`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/backup/settings`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setBackups(backupsRes.data.backups || []);
      if (settingsRes.data) {
        setBackupSettings({
          daily_backup_enabled: settingsRes.data.daily_backup_enabled !== false,
          retention_days: settingsRes.data.retention_days || 30
        });
      }
    } catch (error) {
      console.error('Failed to fetch backups:', error);
    } finally {
      setIsLoadingBackups(false);
    }
  };

  const handleCreateBackup = async () => {
    setIsCreatingBackup(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/backup/create`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Backup created successfully! ID: ${response.data.backup_id}`);
      fetchBackups();
    } catch (error) {
      toast.error('Failed to create backup: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsCreatingBackup(false);
    }
  };

  const handleRestoreBackup = async (backupId, options = {}) => {
    const warningMessage = options.restore_users || options.restore_transactions
      ? '⚠️ CRITICAL WARNING: You are about to restore user accounts and/or transaction data. This will PERMANENTLY REPLACE current data. Are you absolutely sure?'
      : '⚠️ WARNING: This will overwrite current settings with the backup data. Are you sure you want to restore this backup?';
    
    if (!window.confirm(warningMessage)) {
      return;
    }
    setIsRestoring(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (options.restore_users) params.append('restore_users', 'true');
      if (options.restore_transactions) params.append('restore_transactions', 'true');
      
      await axios.post(`${API}/admin/backup/${backupId}/restore?${params.toString()}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Backup restored successfully! Refreshing page...');
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      toast.error('Failed to restore backup: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsRestoring(false);
    }
  };

  const handleUpdateBackupSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/backup/settings?daily_backup_enabled=${backupSettings.daily_backup_enabled}&retention_days=${backupSettings.retention_days}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Backup settings updated');
    } catch (error) {
      toast.error('Failed to update backup settings: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteBackup = async (backupId) => {
    if (!window.confirm('Are you sure you want to delete this backup?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/backup/${backupId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Backup deleted');
      fetchBackups();
    } catch (error) {
      toast.error('Failed to delete backup');
    }
  };

  // Client-side validation helpers
  const validateMapsSettings = () => {
    const errors = [];
    if (maps.enabled && maps.provider === 'google' && !maps.api_key) {
      errors.push('Google Maps API Key is required when Google Maps is enabled');
    }
    if (maps.api_key && !maps.api_key.startsWith('AIza')) {
      errors.push('Google Maps API Key should start with "AIza"');
    }
    return errors;
  };

  const validateFirebaseSettings = () => {
    const errors = [];
    if (firebase.enabled) {
      if (!firebase.api_key) errors.push('Firebase API Key is required');
      if (!firebase.project_id) errors.push('Firebase Project ID is required');
      if (!firebase.messaging_sender_id) errors.push('Messaging Sender ID is required');
      if (!firebase.app_id) errors.push('Firebase App ID is required');
      if (!firebase.vapid_key) errors.push('VAPID Key is required for web push');
      if (!firebase.service_account_json && !firebase.server_key) {
        errors.push('Either Service Account JSON or Server Key is required for backend notifications');
      }
    }
    return errors;
  };

  const handleSaveCommission = async () => {
    setIsSaving(true);
    try {
      await api.updateCommission({
        global_rate: commission.global_rate / 100,
        category_rates: commission.category_rates,
        enabled: commission.enabled
      });
      toast.success('Commission settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveSMTP = async () => {
    setIsSaving(true);
    try {
      await api.updateSMTP(smtp);
      toast.success('SMTP settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveResend = async () => {
    if (resend.enabled && (!resend.api_key || !resend.from_email)) {
      toast.error('Resend API Key and From Email are required when enabled');
      return;
    }
    setIsSaving(true);
    try {
      await api.updateResend(resend);
      toast.success('Resend settings saved!');
    } catch (error) {
      toast.error('Failed to save Resend settings: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendResendTest = async () => {
    if (!resendTestEmail) {
      toast.error('Please enter an email address');
      return;
    }
    setIsSendingResendTest(true);
    try {
      const response = await axios.post(`${API}/admin/settings/resend/test?to_email=${encodeURIComponent(resendTestEmail)}`);
      toast.success(response.data.message || 'Test email sent! Check your inbox.');
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMsg = 'Failed to send test email';
      if (typeof detail === 'string') errorMsg = detail;
      else if (Array.isArray(detail)) errorMsg = detail.map((d) => d?.msg).filter(Boolean).join(', ') || errorMsg;
      else if (detail && typeof detail === 'object') errorMsg = detail.msg || JSON.stringify(detail);
      toast.error(errorMsg);
    } finally {
      setIsSendingResendTest(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address');
      return;
    }
    setIsSendingTest(true);
    try {
      const response = await axios.post(`${API}/admin/settings/smtp/test?to_email=${encodeURIComponent(testEmail)}`);
      toast.success(response.data.message || 'Test email sent! Check your inbox.');
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMsg = 'Failed to send test email';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map((d) => d?.msg).filter(Boolean).join(', ') || errorMsg;
      } else if (detail && typeof detail === 'object') {
        errorMsg = detail.msg || JSON.stringify(detail);
      }
      toast.error(errorMsg);
    } finally {
      setIsSendingTest(false);
    }
  };

  const handleSaveSMS = async () => {
    setIsSaving(true);
    try {
      await api.updateSMS(sms);
      toast.success('SMS settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveMaintenance = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/maintenance`, maintenance, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(maintenance.enabled ? 'Site is now in maintenance mode!' : 'Maintenance mode disabled');
    } catch (error) {
      console.error('Maintenance save error:', error.response?.data || error);
      toast.error('Failed to save maintenance settings: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSavePayMongo = async () => {
    setIsSaving(true);
    try {
      await api.updatePayMongo(paymongo);
      toast.success('PayMongo settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveStorage = async () => {
    setIsSaving(true);
    try {
      await api.updateStorage(storage);
      toast.success('Storage settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveMaps = async () => {
    // Client-side validation
    const validationErrors = validateMapsSettings();
    if (validationErrors.length > 0) {
      validationErrors.forEach(err => toast.error(err));
      return;
    }
    
    setIsSaving(true);
    setLastSaveResponse(null);
    try {
      const response = await api.updateMaps(maps);
      setLastSaveResponse({ type: 'maps', success: true, data: response.data });
      toast.success('Maps settings saved!', {
        description: `Provider: ${maps.provider}, Enabled: ${maps.enabled ? 'Yes' : 'No'}`
      });
    } catch (error) {
      const errorDetail = error.response?.data?.detail || error.message || 'Unknown error';
      setLastSaveResponse({ type: 'maps', success: false, error: errorDetail });
      toast.error('Failed to save Maps settings', {
        description: errorDetail,
        duration: 8000
      });
      console.error('Maps save error:', error.response || error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveFirebase = async () => {
    // Client-side validation
    const validationErrors = validateFirebaseSettings();
    if (validationErrors.length > 0) {
      validationErrors.forEach(err => toast.error(err));
      return;
    }
    
    setIsSaving(true);
    setLastSaveResponse(null);
    try {
      const response = await api.updateFirebase(firebase);
      setLastSaveResponse({ type: 'firebase', success: true, data: response.data });
      toast.success('Firebase settings saved!', {
        description: `Project: ${firebase.project_id || 'N/A'}, Enabled: ${firebase.enabled ? 'Yes' : 'No'}`
      });
    } catch (error) {
      const errorDetail = error.response?.data?.detail || error.message || 'Unknown error';
      setLastSaveResponse({ type: 'firebase', success: false, error: errorDetail });
      toast.error('Failed to save Firebase settings', {
        description: errorDetail,
        duration: 8000
      });
      console.error('Firebase save error:', error.response || error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveServiceArea = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/admin/settings/service-area?max_radius_km=${serviceArea.max_radius_km}&default_radius_km=${serviceArea.default_radius_km}`);
      toast.success('Service area config saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveReferral = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/referral/settings`, referral, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Referral settings saved!');
    } catch (error) {
      toast.error('Failed to save referral settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveProviderLoyalty = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/provider-loyalty/settings`, providerLoyalty, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Provider loyalty settings saved!');
    } catch (error) {
      toast.error('Failed to save provider loyalty settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveInsurance = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/insurance/settings`, insurance, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Insurance settings saved!');
    } catch (error) {
      toast.error('Failed to save insurance settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveBulkDiscount = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/bulk-discount/settings`, bulkDiscount, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bulk discount settings saved!');
    } catch (error) {
      toast.error('Failed to save bulk discount settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveTax = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/tax-settings`, tax, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tax settings saved!');
    } catch (error) {
      toast.error('Failed to save tax settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveWallet = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/wallet-settings`, wallet, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Wallet settings saved!');
    } catch (error) {
      toast.error('Failed to save wallet settings');
    } finally {
      setIsSaving(false);
    }
  };

  const addDiscountTier = () => {
    setBulkDiscount(prev => ({
      ...prev,
      tiers: [...prev.tiers, { min_units: prev.tiers.length > 0 ? prev.tiers[prev.tiers.length - 1].min_units + 2 : 3, discount_percentage: 5 }]
    }));
  };

  const updateDiscountTier = (index, field, value) => {
    setBulkDiscount(prev => ({
      ...prev,
      tiers: prev.tiers.map((tier, i) => i === index ? { ...tier, [field]: value } : tier)
    }));
  };

  const removeDiscountTier = (index) => {
    setBulkDiscount(prev => ({
      ...prev,
      tiers: prev.tiers.filter((_, i) => i !== index)
    }));
  };

  const addLoyaltyTier = () => {
    setProviderLoyalty(prev => ({
      ...prev,
      tiers: [...prev.tiers, { name: 'New Tier', min_jobs: 0, discount_percentage: 0, priority_booking: false, badge_color: 'blue' }]
    }));
  };

  const updateTier = (index, field, value) => {
    setProviderLoyalty(prev => ({
      ...prev,
      tiers: prev.tiers.map((tier, i) => i === index ? { ...tier, [field]: value } : tier)
    }));
  };

  const removeTier = (index) => {
    setProviderLoyalty(prev => ({
      ...prev,
      tiers: prev.tiers.filter((_, i) => i !== index)
    }));
  };

  const IntegrationStatus = ({ enabled, label }) => (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
      enabled ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-500'
    }`}>
      {enabled ? <CheckCircle className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
      {enabled ? 'Active' : 'Inactive'}
    </div>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <AdminSidebar />
      <AdminMobileNav />

      {/* Main Content */}
      <main className="flex-1 p-4 md:p-8 md:ml-64 pt-16 md:pt-8 overflow-x-hidden">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button 
            onClick={() => navigate('/admin')}
            className="w-10 h-10 rounded-full hover:bg-stone-200 flex items-center justify-center md:hidden"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Poppins]">Settings & Integrations</h1>
            <p className="text-slate-500">Configure platform settings and API integrations</p>
          </div>
        </div>

        {/* Integration Overview Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-8">
          <button 
            onClick={() => setActiveTab('paymongo')}
            className={`p-4 rounded-xl border-2 transition-all ${activeTab === 'paymongo' ? 'border-amber-500 bg-amber-50' : 'border-stone-200 bg-white hover:border-stone-300'}`}
          >
            <CreditCard className={`w-6 h-6 mb-2 ${paymongo.enabled ? 'text-green-600' : 'text-stone-400'}`} />
            <p className="text-xs font-medium text-slate-700">Payments</p>
            <p className="text-[10px] text-slate-500">{paymongo.enabled ? 'Connected' : 'Not Set'}</p>
          </button>
          
          <button 
            onClick={() => setActiveTab('storage')}
            className={`p-4 rounded-xl border-2 transition-all ${activeTab === 'storage' ? 'border-amber-500 bg-amber-50' : 'border-stone-200 bg-white hover:border-stone-300'}`}
          >
            <HardDrive className={`w-6 h-6 mb-2 ${(storage.enabled || storage.use_emergent_storage) ? 'text-green-600' : 'text-stone-400'}`} />
            <p className="text-xs font-medium text-slate-700">Storage</p>
            <p className={`text-[10px] ${(storage.enabled || storage.use_emergent_storage) ? 'text-green-600 font-medium' : 'text-slate-500'}`}>{storage.use_emergent_storage ? 'Emergent' : storage.provider}</p>
          </button>
          
          <button 
            onClick={() => setActiveTab('sms')}
            className={`p-4 rounded-xl border-2 transition-all ${activeTab === 'sms' ? 'border-amber-500 bg-amber-50' : 'border-stone-200 bg-white hover:border-stone-300'}`}
          >
            <MessageSquare className={`w-6 h-6 mb-2 ${sms.enabled ? 'text-green-600' : 'text-stone-400'}`} />
            <p className="text-xs font-medium text-slate-700">SMS</p>
            <p className="text-[10px] text-slate-500">{sms.enabled ? sms.provider : 'Not Set'}</p>
          </button>
          
          <button 
            onClick={() => setActiveTab('email')}
            className={`p-4 rounded-xl border-2 transition-all ${activeTab === 'email' ? 'border-amber-500 bg-amber-50' : 'border-stone-200 bg-white hover:border-stone-300'}`}
          >
            <Mail className={`w-6 h-6 mb-2 ${smtp.enabled ? 'text-green-600' : 'text-stone-400'}`} />
            <p className="text-xs font-medium text-slate-700">Email</p>
            <p className="text-[10px] text-slate-500">{smtp.enabled ? 'Connected' : 'Not Set'}</p>
          </button>
          
          <button 
            onClick={() => setActiveTab('maps')}
            className={`p-4 rounded-xl border-2 transition-all ${activeTab === 'maps' ? 'border-amber-500 bg-amber-50' : 'border-stone-200 bg-white hover:border-stone-300'}`}
          >
            <MapPin className={`w-6 h-6 mb-2 ${maps.enabled ? 'text-green-600' : 'text-stone-400'}`} />
            <p className="text-xs font-medium text-slate-700">Maps</p>
            <p className="text-[10px] text-slate-500">{maps.enabled ? maps.provider : 'Not Set'}</p>
          </button>
          
          <button 
            onClick={() => setActiveTab('commission')}
            className={`p-4 rounded-xl border-2 transition-all ${activeTab === 'commission' ? 'border-amber-500 bg-amber-50' : 'border-stone-200 bg-white hover:border-stone-300'}`}
          >
            <Percent className={`w-6 h-6 mb-2 text-amber-600`} />
            <p className="text-xs font-medium text-slate-700">Commission</p>
            <p className="text-[10px] text-slate-500">{commission.global_rate}%</p>
          </button>
          
          <button 
            onClick={() => setActiveTab('firebase')}
            className={`p-4 rounded-xl border-2 transition-all ${activeTab === 'firebase' ? 'border-amber-500 bg-amber-50' : 'border-stone-200 bg-white hover:border-stone-300'}`}
          >
            <Bell className={`w-6 h-6 mb-2 ${firebase.enabled ? 'text-green-600' : 'text-stone-400'}`} />
            <p className="text-xs font-medium text-slate-700">Push</p>
            <p className="text-[10px] text-slate-500">{firebase.enabled ? 'Connected' : 'Not Set'}</p>
          </button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6 flex-wrap h-auto gap-1">
            <TabsTrigger value="commission" data-testid="commission-tab">
              <Percent className="w-4 h-4 mr-2" />
              Commission
            </TabsTrigger>
            <TabsTrigger value="paymongo" data-testid="paymongo-tab">
              <CreditCard className="w-4 h-4 mr-2" />
              PayMongo
            </TabsTrigger>
            <TabsTrigger value="storage" data-testid="storage-tab">
              <HardDrive className="w-4 h-4 mr-2" />
              Storage
            </TabsTrigger>
            <TabsTrigger value="sms" data-testid="sms-tab">
              <MessageSquare className="w-4 h-4 mr-2" />
              SMS
            </TabsTrigger>
            <TabsTrigger value="email" data-testid="email-tab">
              <Mail className="w-4 h-4 mr-2" />
              Email
            </TabsTrigger>
            <TabsTrigger value="maps" data-testid="maps-tab">
              <MapPin className="w-4 h-4 mr-2" />
              Maps
            </TabsTrigger>
            <TabsTrigger value="firebase" data-testid="firebase-tab">
              <Bell className="w-4 h-4 mr-2" />
              Push Notifications
            </TabsTrigger>
            <TabsTrigger value="service-area" data-testid="service-area-tab">
              <MapPin className="w-4 h-4 mr-2" />
              Service Area
            </TabsTrigger>
            <TabsTrigger value="maintenance" data-testid="maintenance-tab">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Maintenance
            </TabsTrigger>
            <TabsTrigger value="referral" data-testid="referral-tab">
              <Gift className="w-4 h-4 mr-2" />
              Referral
            </TabsTrigger>
            <TabsTrigger value="provider-loyalty" data-testid="provider-loyalty-tab">
              <Crown className="w-4 h-4 mr-2" />
              Provider Loyalty
            </TabsTrigger>
            <TabsTrigger value="insurance" data-testid="insurance-tab">
              <Shield className="w-4 h-4 mr-2" />
              Insurance
            </TabsTrigger>
            <TabsTrigger value="bulk-discount" data-testid="bulk-discount-tab">
              <Percent className="w-4 h-4 mr-2" />
              Bulk Discount
            </TabsTrigger>
            <TabsTrigger value="tax" data-testid="tax-tab">
              <Percent className="w-4 h-4 mr-2" />
              Tax
            </TabsTrigger>
            <TabsTrigger value="wallet" data-testid="wallet-tab">
              <Wallet className="w-4 h-4 mr-2" />
              Wallet
            </TabsTrigger>
            <TabsTrigger value="signup" data-testid="signup-tab">
              <UserPlus className="w-4 h-4 mr-2" />
              Signup
            </TabsTrigger>
          </TabsList>

          {/* Commission Settings */}
          <TabsContent value="commission">
            <CommissionSettings
              data={commission}
              setData={setCommission}
              isSaving={isSaving}
              onSave={handleSaveCommission}
            />
          </TabsContent>

          {/* PayMongo Settings */}
          <TabsContent value="paymongo">
            <PaymongoSettings
              data={paymongo}
              setData={setPayMongo}
              isSaving={isSaving}
              onSave={handleSavePayMongo}
              IntegrationStatus={IntegrationStatus}
            />
          </TabsContent>

          {/* Storage Settings */}
          <TabsContent value="storage">
            <StorageSettings
              data={storage}
              setData={setStorage}
              isSaving={isSaving}
              onSave={handleSaveStorage}
              IntegrationStatus={IntegrationStatus}
            />
          </TabsContent>

          {/* SMS Settings */}
          <TabsContent value="sms">
            <SMSSettings
              data={sms}
              setData={setSMS}
              isSaving={isSaving}
              onSave={handleSaveSMS}
              IntegrationStatus={IntegrationStatus}
            />
          </TabsContent>

          {/* Email Settings */}
          <TabsContent value="email">
            <EmailSettings
              data={smtp}
              setData={setSMTP}
              isSaving={isSaving}
              onSave={handleSaveSMTP}
              IntegrationStatus={IntegrationStatus}
              testEmail={testEmail}
              setTestEmail={setTestEmail}
              isSendingTest={isSendingTest}
              onSendTestEmail={handleSendTestEmail}
              resendData={resend}
              setResendData={setResend}
              onSaveResend={handleSaveResend}
              resendTestEmail={resendTestEmail}
              setResendTestEmail={setResendTestEmail}
              isSendingResendTest={isSendingResendTest}
              onSendResendTest={handleSendResendTest}
            />
          </TabsContent>

          {/* Maps Settings */}
          <TabsContent value="maps">
            <MapsSettings
              data={maps}
              setData={setMaps}
              isSaving={isSaving}
              onSave={handleSaveMaps}
              IntegrationStatus={IntegrationStatus}
            />
          </TabsContent>

          {/* Firebase Push Notification Settings */}
          <TabsContent value="firebase">
            <FirebaseSettings
              data={firebase}
              setData={setFirebase}
              isSaving={isSaving}
              onSave={handleSaveFirebase}
              IntegrationStatus={IntegrationStatus}
            />
          </TabsContent>

          {/* Service Area Settings */}
          <TabsContent value="service-area">
            <ServiceAreaSettings
              data={serviceArea}
              setData={setServiceArea}
              isSaving={isSaving}
              onSave={handleSaveServiceArea}
            />
          </TabsContent>

          {/* Maintenance Mode */}
          <TabsContent value="maintenance">
            <MaintenanceSettings
              data={maintenance}
              setData={setMaintenance}
              isSaving={isSaving}
              onSave={handleSaveMaintenance}
            />
          </TabsContent>

          {/* Referral Program Settings */}
          <TabsContent value="referral">
            <ReferralSettings
              data={referral}
              setData={setReferral}
              isSaving={isSaving}
              onSave={handleSaveReferral}
            />
          </TabsContent>

          {/* Provider Loyalty Program Settings */}
          <TabsContent value="provider-loyalty">
            <ProviderLoyaltySettings
              data={providerLoyalty}
              setData={setProviderLoyalty}
              isSaving={isSaving}
              onSave={handleSaveProviderLoyalty}
              addTier={addLoyaltyTier}
              updateTier={updateTier}
              removeTier={removeTier}
            />
          </TabsContent>

          {/* Insurance Settings */}
          <TabsContent value="insurance">
            <InsuranceSettings
              data={insurance}
              setData={setInsurance}
              isSaving={isSaving}
              onSave={handleSaveInsurance}
            />
          </TabsContent>

          {/* Bulk Discount Settings */}
          <TabsContent value="bulk-discount">
            <BulkDiscountSettings
              data={bulkDiscount}
              setData={setBulkDiscount}
              isSaving={isSaving}
              onSave={handleSaveBulkDiscount}
              addTier={addDiscountTier}
              updateTier={updateDiscountTier}
              removeTier={removeDiscountTier}
            />
          </TabsContent>

          {/* Tax Settings */}
          <TabsContent value="tax">
            <TaxSettings
              data={tax}
              setData={setTax}
              isSaving={isSaving}
              onSave={handleSaveTax}
            />
          </TabsContent>

          {/* Wallet Settings */}
          <TabsContent value="wallet">
            <WalletSettings
              data={wallet}
              setData={setWallet}
              isSaving={isSaving}
              onSave={handleSaveWallet}
            />
          </TabsContent>

          {/* Signup Settings */}
          <TabsContent value="signup">
            <SignupSettings />
          </TabsContent>
        </Tabs>

        {/* Debug Panel - Collapsible */}
        <div className="mt-8 border-t border-stone-200 pt-6">
          <button
            onClick={() => setShowDebugPanel(!showDebugPanel)}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4"
            data-testid="toggle-debug-panel"
          >
            <Bug className="w-5 h-5" />
            <span className="font-medium">Debugging Tools</span>
            {showDebugPanel ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {showDebugPanel && (
            <div className="bg-slate-900 rounded-2xl p-6 text-white space-y-6">
              {/* Health Check Button */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <Activity className="w-5 h-5 text-green-400" />
                    Settings Health Check
                  </h3>
                  <p className="text-slate-400 text-sm mt-1">
                    Run a diagnostic check on all integration settings to verify they are saved correctly.
                  </p>
                </div>
                <Button
                  onClick={handleHealthCheck}
                  disabled={isCheckingHealth}
                  className="bg-green-600 hover:bg-green-500 text-white whitespace-nowrap"
                  data-testid="run-health-check-btn"
                >
                  {isCheckingHealth ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Checking...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Run Health Check
                    </>
                  )}
                </Button>
              </div>

              {/* Last Save Response */}
              {lastSaveResponse && (
                <div className={`p-4 rounded-xl ${lastSaveResponse.success ? 'bg-green-900/50 border border-green-700' : 'bg-red-900/50 border border-red-700'}`}>
                  <h4 className="font-medium flex items-center gap-2 mb-2">
                    {lastSaveResponse.success ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                    )}
                    Last Save Result ({lastSaveResponse.type})
                  </h4>
                  <pre className="text-xs text-slate-300 overflow-auto max-h-32 whitespace-pre-wrap">
                    {JSON.stringify(lastSaveResponse.success ? lastSaveResponse.data : { error: lastSaveResponse.error }, null, 2)}
                  </pre>
                </div>
              )}

              {/* Health Check Results */}
              {healthData && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium flex items-center gap-2">
                      <Database className="w-4 h-4 text-blue-400" />
                      Health Check Results
                    </h4>
                    <span className="text-xs text-slate-500">
                      {healthData.timestamp ? new Date(healthData.timestamp).toLocaleString() : 'N/A'}
                    </span>
                  </div>

                  {healthData.error ? (
                    <div className="bg-red-900/50 border border-red-700 p-4 rounded-xl">
                      <p className="text-red-300">Error: {healthData.error}</p>
                    </div>
                  ) : (
                    <>
                      {/* Database Status */}
                      <div className={`p-3 rounded-lg ${healthData.database?.status === 'connected' ? 'bg-green-900/30' : 'bg-red-900/30'}`}>
                        <div className="flex items-center gap-2">
                          <Server className="w-4 h-4" />
                          <span>Database: </span>
                          <span className={healthData.database?.status === 'connected' ? 'text-green-400' : 'text-red-400'}>
                            {healthData.database?.status || 'Unknown'}
                          </span>
                          {healthData.database?.name && (
                            <span className="text-slate-500 text-sm">({healthData.database.name})</span>
                          )}
                        </div>
                      </div>

                      {/* Integration Status Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {healthData.integrations && Object.entries(healthData.integrations).map(([key, value]) => (
                          <div
                            key={key}
                            className={`p-3 rounded-lg border ${
                              value.status === 'configured' && value.enabled
                                ? 'bg-green-900/20 border-green-700'
                                : value.status === 'configured'
                                ? 'bg-yellow-900/20 border-yellow-700'
                                : 'bg-slate-800 border-slate-700'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium capitalize">{key}</span>
                              {value.enabled ? (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-green-600 text-white">Enabled</span>
                              ) : (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-slate-600 text-slate-300">Disabled</span>
                              )}
                            </div>
                            <p className="text-xs text-slate-400">
                              Status: {value.status || 'Unknown'}
                            </p>
                            {value.updated_at && (
                              <p className="text-xs text-slate-500">
                                Updated: {new Date(value.updated_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Detailed Config (Expandable) */}
                      <details className="mt-4">
                        <summary className="cursor-pointer text-sm text-slate-400 hover:text-slate-200">
                          View Full Debug Data (click to expand)
                        </summary>
                        <pre className="mt-2 text-xs text-slate-400 overflow-auto max-h-96 bg-slate-950 p-4 rounded-lg">
                          {JSON.stringify(healthData, null, 2)}
                        </pre>
                      </details>
                    </>
                  )}
                </div>
              )}

              {/* Help Text */}
              <div className="bg-blue-900/30 border border-blue-700 rounded-xl p-4">
                <h4 className="font-medium text-blue-300 mb-2">Debugging Tips:</h4>
                <ul className="text-sm text-blue-200 space-y-1 list-disc list-inside">
                  <li>Run the health check after saving settings to verify they were stored.</li>
                  <li>If settings show as "not_configured", they haven't been saved yet.</li>
                  <li>Green "Enabled" badge means the integration is active.</li>
                  <li>Check your browser's console (F12) for detailed API responses.</li>
                  <li>API keys are masked for security - "***SET***" means a value exists.</li>
                </ul>
              </div>

              {/* Backup & Restore Section */}
              <div className="border-t border-slate-700 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      <Database className="w-5 h-5 text-purple-400" />
                      Backup & Disaster Recovery
                    </h3>
                    <p className="text-slate-400 text-sm mt-1">
                      Full backups include settings, categories, services, users, bookings, and transactions.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={fetchBackups}
                      disabled={isLoadingBackups}
                      variant="outline"
                      className="border-slate-600 text-slate-300 hover:bg-slate-800"
                    >
                      {isLoadingBackups ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    </Button>
                    <Button
                      onClick={handleCreateBackup}
                      disabled={isCreatingBackup}
                      className="bg-purple-600 hover:bg-purple-500 text-white"
                    >
                      {isCreatingBackup ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</>
                      ) : (
                        <><Plus className="w-4 h-4 mr-2" />Create Full Backup</>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Daily Backup Settings */}
                <div className="mb-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="daily-backup"
                        checked={backupSettings.daily_backup_enabled}
                        onChange={(e) => setBackupSettings({...backupSettings, daily_backup_enabled: e.target.checked})}
                        className="w-4 h-4 rounded text-purple-600 bg-slate-700 border-slate-600"
                      />
                      <label htmlFor="daily-backup" className="text-sm text-white">
                        Enable Daily Automatic Backups
                        <span className="block text-xs text-slate-400">Runs at 3:00 AM PHT daily</span>
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-slate-400">Keep last</label>
                      <select
                        value={backupSettings.retention_days}
                        onChange={(e) => setBackupSettings({...backupSettings, retention_days: parseInt(e.target.value)})}
                        className="bg-slate-700 border border-slate-600 rounded px-2 py-1 text-sm text-white"
                      >
                        <option value={7}>7 days</option>
                        <option value={14}>14 days</option>
                        <option value={30}>30 days</option>
                        <option value={60}>60 days</option>
                        <option value={90}>90 days</option>
                      </select>
                      <Button
                        onClick={handleUpdateBackupSettings}
                        size="sm"
                        className="bg-slate-600 hover:bg-slate-500 text-white text-xs"
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Backup List */}
                {backups.length > 0 ? (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {backups.map((backup) => (
                      <div key={backup.id} className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-white">{backup.id}</p>
                            {backup.backup_type === 'daily_auto' && (
                              <span className="px-2 py-0.5 text-xs bg-blue-600/30 text-blue-300 rounded">Auto</span>
                            )}
                            {backup.backup_type === 'manual' && (
                              <span className="px-2 py-0.5 text-xs bg-green-600/30 text-green-300 rounded">Manual</span>
                            )}
                          </div>
                          <p className="text-xs text-slate-400">
                            {new Date(backup.created_at).toLocaleString()} • By: {backup.created_by_email || 'System'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <select
                            className="bg-slate-700 border border-slate-600 rounded px-2 py-1 text-xs text-white"
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === 'settings') handleRestoreBackup(backup.id, {});
                              else if (val === 'full') handleRestoreBackup(backup.id, { restore_users: true, restore_transactions: true });
                              else if (val === 'users') handleRestoreBackup(backup.id, { restore_users: true });
                              else if (val === 'transactions') handleRestoreBackup(backup.id, { restore_transactions: true });
                              e.target.value = '';
                            }}
                            disabled={isRestoring}
                            defaultValue=""
                          >
                            <option value="" disabled>Restore...</option>
                            <option value="settings">Settings Only</option>
                            <option value="users">+ Users</option>
                            <option value="transactions">+ Transactions</option>
                            <option value="full">Full Restore</option>
                          </select>
                          <Button
                            onClick={() => handleDeleteBackup(backup.id)}
                            size="sm"
                            variant="ghost"
                            className="text-red-400 hover:text-red-300 hover:bg-red-900/30 text-xs"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-slate-500 border-2 border-dashed border-slate-700 rounded-xl">
                    <Database className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No backups found</p>
                    <p className="text-xs mt-1">Click "Create Full Backup" to save your current data</p>
                  </div>
                )}

                {/* Backup info */}
                <div className="mt-4 p-3 bg-purple-900/30 border border-purple-700 rounded-lg">
                  <p className="text-sm text-purple-200">
                    <strong>📦 What's backed up:</strong> Admin settings, categories, services, CMS content, users, provider profiles, bookings, transactions, reviews, and wallets.
                  </p>
                  <p className="text-xs text-purple-300 mt-2">
                    <strong>⚠️ Note:</strong> "Settings Only" restore won't affect user accounts or transactions. Use "Full Restore" only for disaster recovery.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
